import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { REALTIME_ENABLED } from '@/lib/realtime';
import { useViewerRef } from '@/hooks/useViewerRef';
import { getRealtime } from '@/features/chat/api';
import {
  onShellEvidenceChange,
  reportPresence,
  shellWindowIsHidden,
} from '@/features/presence/systemActivity';
import { applyRealtimeEvent } from '@/features/chat/lib/applyEvent';
import { typingStore } from '@/features/chat/store/typingStore';
import { applyEventToCalendar } from '@/features/calendar/lib/applyToCalendar';
import { applyEventToMeeting } from '@/features/meeting/lib/applyToMeeting';
import { applyEventToTasks } from '@/features/tasks/lib/applyToTasks';
import { applyEventToVoice } from '@/features/voice/lib/applyToVoice';
import { applyEventToPosts } from './applyToPosts';
import { applyEventToShell } from './applyToShell';
import { useTopicReset } from './useTopicReset';

/**
 * Headless. Mount once, inside the query provider.
 *
 * ONE socket, ONE reducer: every inbound event is folded into the TanStack
 * cache here rather than in each screen, so an open chat, a channel feed and
 * the sidebar all converge from the same frame — and nothing refetches to find
 * out something it was already told.
 *
 * It attaches to `getRealtime()` (not the live client directly) so the offline
 * mock transport drives exactly the same reducer.
 */
export function RealtimeRuntime() {
  const client = useQueryClient();
  const viewerId = useViewerRef()?.id ?? null;

  useTopicReset();

  useEffect(() => {
    const realtime = getRealtime();
    realtime.connect();
    const stopSweeper = typingStore.startSweeper();

    const off = realtime.onEvent(({ event }) => {
      applyRealtimeEvent(client, event, viewerId);
      applyEventToPosts(client, event, viewerId);
      applyEventToShell(client, event);
      applyEventToVoice(client, event, viewerId);
      applyEventToMeeting(client, event);
      applyEventToCalendar(client, event);
      applyEventToTasks(client, event);
    });

    return () => {
      off();
      stopSweeper();
    };
  }, [client, viewerId]);

  /**
   * Visibility hints replace any need for an activity poll (protocol §4).
   *
   * `background` is a SCHEDULING fact, not a presence one. The gateway uses it
   * to grant this socket extended heartbeat grace and to refresh the presence
   * TTL — a hidden tab's timers get throttled to roughly once a minute — but it
   * deliberately leaves presence alone. Whether the user is Away is decided by
   * `ping.idleMs` against real input: a minimised window is not Away, and it is
   * certainly not Offline while the socket is still up. Becoming visible
   * reports at once so the right answer arrives without waiting for a ping -
   * `active` if a human is there to be reported, `idle` if the evidence says
   * the window came back and its owner did not.
   *
   * "REAL INPUT" MEANS SOMETHING DIFFERENT ON EACH PLATFORM, and a comment here
   * used to say this was how Teams does it without saying which. In a BROWSER
   * it is DOM input into this document and nothing more, which is all a tab can
   * ever see. On the DESKTOP the shell reports OS-level idle
   * (`powerMonitor.getSystemIdleTime()`, GetLastInputInfo on Windows) - the
   * fact Teams actually uses - so working in another application keeps this
   * client Available. See features/presence/hooks/useActivityMonitor.ts.
   */
  useEffect(() => {
    if (!REALTIME_ENABLED) return undefined;

    /*
     * THE DESKTOP SHELL CANNOT USE `visibilityState`, AND THAT COST IT THE
     * GENTLER REAP.
     *
     * The workspace window is created with `backgroundThrottling: false`
     * (apps/desktop/src/main/windows.ts), which Electron's `webPreferences`
     * documentation notes "also affects the Page Visibility API": the state is
     * pinned at `visible` for the life of the window. Minimise the app, or send
     * it to the tray, and this document is never told - so it went on reporting
     * `active` and NEVER sent `background`.
     *
     * The consequence is not cosmetic. `background` is what moves a connection
     * off the 60 s foreground reap (`appSilentMs > HEARTBEAT_TIMEOUT_MS`) and
     * onto the 120 s transport grace, where application silence is worth Away
     * and never Offline (apps/gateway/src/core/heartbeat.ts). Without it the
     * desktop client - the one that is meant to beat a browser tab - sat
     * permanently on the strictest reap branch in the system, while a hidden
     * tab, which CAN report `background`, got the gentler one. Any stall over a
     * minute cost it the socket, and the user read Offline.
     *
     * The shell can see the truth in one call and sends it beside the OS idle
     * time; `shellWindowIsHidden()` is that answer, and it is false in a
     * browser and in a shell too old to report it.
     */
    /*
     * IT USED TO SAY `active` WHENEVER THE WINDOW WAS SHOWING, AND THAT WAS THE
     * DEFECT, NOT THE FIX.
     *
     * A window being on screen is a fact about a WINDOW. `active` is a claim
     * about a PERSON: the gateway answers it with `applyActivity(conn, true)`,
     * which clears F_IDLE for everybody. This function runs on mount, on every
     * reconnect, on every visibility change and on every report from the shell,
     * and none of those is somebody touching a keyboard. A locked workstation
     * is on screen. A reload beside an empty desk is on screen. A machine woken
     * at 03:00 by Windows Update, with the window Windows helpfully restored, is
     * on screen. All three used to send `active` - and, worse, `active` also
     * stamped the transport's idle clock at NOW, so the OS could not correct
     * them for the next five minutes.
     *
     * So the claim is worked out from the evidence, in the one place that holds
     * it, and the connection is repaired on its own terms: a window that is
     * showing wants its socket back whether or not a human is in front of it.
     */
    const report = (force = false): void => {
      /* Coming back is also the moment to repair the transport. A window the
         browser froze for long enough runs no reconnect backoff while it is
         frozen and delivers its socket's `close` only on the way back, so
         waking up without this leaves the user reading Offline to everyone else
         for a backoff interval they did nothing to earn. `connect()` is a no-op
         on a healthy socket, so the visible path costs nothing. */
      if (document.visibilityState === 'visible' && !shellWindowIsHidden()) {
        getRealtime().connect();
      }
      reportPresence(force);
    };

    /* A new socket believes nothing, so the first assertion is forced. */
    report(true);
    /* ONE OWNER FOR THE `presence` FRAME. The shell's reports change what
       `report()` answers, so they re-run IT rather than sending frames of their
       own - which is also what keeps the reconnect re-assertion below correct
       for a window that was already minimised when the socket came back. */
    const offShellWindow = onShellEvidenceChange(report);
    const onDomChange = (): void => report();
    document.addEventListener('visibilitychange', onDomChange);
    /* Page Lifecycle: fired when a frozen page is thawed, which does not always
       come with a visibility change. */
    document.addEventListener('resume', onDomChange);

    /*
     * A hint lives on the CONNECTION that received it, so every new socket
     * starts out foreground — and `visibilitychange` does not fire on a
     * reconnect, because nothing about the page's visibility changed.
     *
     * That gap is a standing Offline bug for anyone with a background tab. A
     * hidden tab's timers are throttled to roughly once a minute, so it stops
     * sending the 25 s application ping; without the hint the heartbeat sweep
     * reaps it at HEARTBEAT_TIMEOUT_MS even though the transport is still
     * answering — measured live at 63 s, close 4408 `heartbeat_timeout`. The
     * client then reconnects, still hidden, still silent, and is reaped again:
     * a permanent Offline flap for a user whose socket never actually failed.
     * Reconnects are not rare, either — a token refresh, a network blip or a
     * gateway deploy reconnects every client at once.
     */
    const offState = getRealtime().onState((connection) => {
      if (connection === 'online') report(true);
    });

    return () => {
      offState();
      offShellWindow();
      document.removeEventListener('visibilitychange', onDomChange);
      document.removeEventListener('resume', onDomChange);
    };
  }, []);

  return null;
}
